import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ArrowsExpandIcon, EyeIcon } from '@heroicons/react/outline';
import { Modal } from 'components/Modal';
import { PREVIEW_STATUSES, TrackingPageConfig } from './defaultConfig';

/**
 * Live preview of the shopper-facing tracking page.
 *
 * This frames the real page from track.shipaid.com and pushes config over
 * postMessage rather than re-rendering a local copy of the design. A local copy
 * would drift from the deployed page — which is exactly the failure mode of the
 * Unlayer setup this replaces.
 */

/**
 * Natural viewport widths the preview renders at before being scaled to fit the
 * editor column. Rendering at the editor's own width would put the shopper page
 * into its mobile layout, hiding every desktop-only arrangement.
 */
const VIEWPORT_WIDTHS = { desktop: 1280, mobile: 390 } as const;
/** Height used until the frame reports its own; it then tracks the real page. */
const INITIAL_VIEWPORT_HEIGHT = 900;
/** Guards against a zero-height flash while the frame is still measuring. */
const MIN_VIEWPORT_HEIGHT = 240;

const PREVIEW_READY = 'shipaid:tracking-preview:ready';
const PREVIEW_MESSAGE = 'shipaid:tracking-preview';
const PREVIEW_HEIGHT = 'shipaid:tracking-preview:height';

const STATUS_LABELS: Record<string, string> = {
  PRE_TRANSIT: 'Label created',
  IN_TRANSIT: 'In transit',
  OUT_FOR_DELIVERY: 'Out for delivery',
  DELIVERED: 'Delivered',
  AVAILABLE_FOR_PICKUP: 'Ready for pickup',
  FAILURE: 'Delivery attempt failed',
  ERROR: 'Delivery exception',
  RETURN_TO_SENDER: 'Returning to sender',
  CANCELLED: 'Cancelled',
  UNKNOWN: 'Awaiting carrier scan',
};

function previewOrigin(): string {
  const base =
    process.env.REACT_APP_TRACKING_PORTAL_URL || 'https://track.shipaid.com';
  try {
    return new URL(base).origin;
  } catch {
    return 'https://track.shipaid.com';
  }
}

type SurfaceProps = {
  config: TrackingPageConfig;
  status: string;
  screen: 'result' | 'search';
  device: 'mobile' | 'desktop';
  title: string;
  /** Omitted in the expanded view, which is already full size. */
  onExpand?: () => void;
};

/** One framed, scaled copy of the shopper page. */
const PreviewSurface = ({
  config,
  status,
  screen,
  device,
  title,
  onExpand,
}: SurfaceProps) => {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(INITIAL_VIEWPORT_HEIGHT);

  const origin = previewOrigin();
  const viewportWidth = VIEWPORT_WIDTHS[device];

  const fitToShell = useCallback(() => {
    const available = shellRef.current?.clientWidth ?? 0;
    if (!available) return;
    setScale(Math.min(1, available / viewportWidth));
  }, [viewportWidth]);

  useLayoutEffect(() => {
    fitToShell();
    window.addEventListener('resize', fitToShell);
    return () => window.removeEventListener('resize', fitToShell);
  }, [fitToShell]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return;
      if (event.data?.type === PREVIEW_READY) setReady(true);
      if (
        event.data?.type === PREVIEW_HEIGHT &&
        typeof event.data.height === 'number'
      ) {
        setContentHeight(Math.max(MIN_VIEWPORT_HEIGHT, event.data.height));
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origin]);

  // Buffer until the frame says it is listening, otherwise the first push races
  // the iframe load and is silently dropped.
  useEffect(() => {
    if (!ready) return;
    frameRef.current?.contentWindow?.postMessage(
      { type: PREVIEW_MESSAGE, config, status, screen },
      origin,
    );
  }, [ready, config, status, screen, origin]);

  return (
    <div className="relative rounded-lg border border-[#e1e3e5] bg-[#eef0f2] p-4">
      {onExpand ? (
        <button
          type="button"
          onClick={onExpand}
          aria-label="Expand preview"
          title="Expand preview"
          className="absolute top-2 left-2 z-10 rounded-md border border-[#e1e3e5] bg-white/90 p-1.5 text-[#6d7175] shadow-sm hover:bg-white hover:text-[#202223]"
        >
          <ArrowsExpandIcon className="w-4 h-4" />
        </button>
      ) : null}
      <div ref={shellRef}>
        <div
          className="mx-auto"
          style={{
            width: viewportWidth * scale,
            height: contentHeight * scale,
          }}
        >
          <iframe
            ref={frameRef}
            title={title}
            src={`${origin}/__preview`}
            width={viewportWidth}
            height={contentHeight}
            className="bg-white rounded-lg shadow-sm border border-gray-200"
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              border: 0,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const PreviewPane = ({ config }: { config: TrackingPageConfig }) => {
  const [status, setStatus] = useState<string>('IN_TRANSIT');
  const [screen, setScreen] = useState<'result' | 'search'>('result');
  const [device, setDevice] = useState<'mobile' | 'desktop'>('desktop');
  const [expanded, setExpanded] = useState(false);

  const toolbar = (
    <>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex gap-1">
          {(
            [
              ['result', 'Tracking page'],
              ['search', 'Cover page'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setScreen(value)}
              aria-pressed={screen === value}
              className={`px-3 py-1.5 text-sm rounded-md border ${
                screen === value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex gap-1">
          {(['desktop', 'mobile'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setDevice(option)}
              aria-pressed={device === option}
              className={`px-3 py-1.5 text-sm rounded-md border ${
                device === option
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-300'
              }`}
            >
              {option === 'desktop' ? 'Desktop' : 'Mobile'}
            </button>
          ))}
        </div>
      </div>

      {/* The cover page has no shipment, so a status has no meaning there. */}
      <div className="mb-3 min-h-[34px]">
        {screen === 'result' ? (
          <select
            aria-label="Preview status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-gray-300 rounded-md text-sm py-1.5 px-2 w-full sm:w-auto"
          >
            {PREVIEW_STATUSES.map((value) => (
              <option key={value} value={value}>
                {STATUS_LABELS[value] ?? value}
              </option>
            ))}
          </select>
        ) : null}
      </div>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-3">
        <EyeIcon className="w-4 h-4 text-[#6d7175]" />
        <h3 className="text-sm font-semibold text-[#202223]">Live preview</h3>
        <span className="text-xs text-[#6d7175]">
          What your customers will see
        </span>
      </div>

      {toolbar}

      <PreviewSurface
        config={config}
        status={status}
        screen={screen}
        device={device}
        title="Tracking page preview"
        onExpand={() => setExpanded(true)}
      />

      {/* A second surface rather than moving the first: relocating a live
          iframe in the DOM reloads it and loses the handshake. */}
      {expanded ? (
        <Modal
          open={expanded}
          openModal={() => setExpanded(false)}
          containerClass="w-full max-w-[95vw] !p-0 overflow-hidden"
          iconClassName="text-[#64748b] hover:text-[#0b1c30]"
        >
          <div className="px-6 pb-6 pt-1">
            <h2 className="text-lg font-semibold text-[#0b1c30] mb-4">
              Live preview
            </h2>
            {toolbar}
            <PreviewSurface
              config={config}
              status={status}
              screen={screen}
              device={device}
              title="Tracking page preview (expanded)"
            />
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

export default PreviewPane;
